import React from 'react'
import {
  Button,
  Box,
  CircularProgress,
  Snackbar,
  Paper,
} from '@mui/material'
import API from '@aws-amplify/api'
import MuiAlert from '@mui/material/Alert'
import { useDispatch } from 'react-redux'
import { DatePicker } from '@material-ui/pickers'
import { IOrder } from 'interfaces/IOrder'
import { formatDecimalAuto } from 'shared/numberFormatter'

const lastMonth = new Date()
lastMonth.setMonth(lastMonth.getMonth() - 1)

export default function ExportOrder() {
  const [isLoading, setIsLoading] = React.useState<any>(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [alertMessage, setAlertMessage] = React.useState('')
  const [startDate, setStartDate] = React.useState<Date | null>(lastMonth)
  const [endDate, setEndDate] = React.useState<Date | null>(new Date())

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }
  const onExportClick = () => {
    if (!startDate || !endDate) {
      return
    }

    startDate.setHours(0)
    endDate.setHours(23)
    startDate.setMinutes(0)
    endDate.setMinutes(59)
    startDate.setSeconds(0)
    endDate.setSeconds(59)
    startDate.setMilliseconds(0)
    endDate.setMilliseconds(999)

    let url = `/orders/getOrder`
    const param = {
      queryStringParameters: {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        pageSize: 9999999,
        isNotCancelled: true,
      },
    }

    setIsLoading(true)
    API.get('products', url, param)
      .then((response) => {
        const rows = [
          [
            'DF',
            'Order ID',
            'TGL INV',
            'Nama Sales',
            'MEREK',
            'DKRM VIA',
            'NAMA CUSTOMER',
            'NAMA KLINIK',
            'Alamat',
            'Alamat',
            'KOTA',
            'TELEPHONE',
            'KEMASAN',
            'ITEM',
            'QTY',
            'UNIT',
            '@',
            'AMOUNT',
            'DISCOUNT',
            'NET AMOUNT',
            'VAT 10%',
            'O.KRM',
            'TOTAL',
            'BERAT/pc',
            'TTL BERAT',
            'DUS',
            'HALAL',
            'SEGEL',
            'SHRINK',
            'KODE RUAHAN',
            'NOTIF #',
          ],
        ]
        let counter = 1
        response.orders.forEach((order: IOrder) => {
          order.orderProducts.forEach((orderProduct) => {
            const amount =
              Number(orderProduct.productQty) * orderProduct.unitPrice
            const netAmount = amount - orderProduct.discount

            rows.push([
              String(counter++),
              order.orderDisplayId || '-',
              new Date(order.createdAt).toLocaleDateString('id-ID'),
              order.employeeName || '-',
              order.brandId || '-',
              order.shipmentType || '-',
              order.customerName || '-',
              '', // nama klinik
              `"${order.address1}"`,
              `"${order.address2}"`,
              `"${order.city}"`,
              `"${order.phoneNumber}"`,
              orderProduct.packagingName || '-',
              orderProduct.productName || '-',
              String(orderProduct.productQty) || '-',
              'PCS',
              orderProduct.unitPrice.toLocaleString('id-ID') || '-',
              amount.toLocaleString('id-ID') || '-',
              orderProduct.discount
                ? `(${orderProduct.discount.toLocaleString('id-ID')})`
                : '-',
              netAmount.toLocaleString('id-ID'),
              'INVOICE',
              '-',
              'INVOICE',
              formatDecimalAuto(orderProduct.weight),
              formatDecimalAuto(
                Number(orderProduct.weight) * Number(orderProduct.productQty)
              ),
              orderProduct.box ? 'DS' : '',
              orderProduct.halal ? 'HL' : '',
              orderProduct.seal ? 'SG' : '',
              orderProduct.wrap ? 'SH' : '',
              `${[
                orderProduct.premixId,
                orderProduct.packagingId,
                orderProduct.labelId,
              ]
                .filter((data) => data)
                .join('.')}`,
              orderProduct.notificationNumber || '-',
            ])
          })
        })

        let csvContent = rows.map((e) => e.join('\t')).join('\n')
        const a = document.createElement('a')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        a.href = url
        a.download = 'order.csv'
        a.style.visibility = 'hidden'
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch((error) => {
        setAlertMessage('Gagal export data.')
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Export Order' })
  }, [dispatch])

  return (
    <Box>
      <Paper>
        <Box p={2} pt={0}>
          <DatePicker
            variant="inline"
            format="dd/MM/yyyy"
            margin="normal"
            fullWidth
            label="Start Date"
            value={startDate}
            onChange={(value) => setStartDate(value)}
            placeholder={`${new Date().getUTCDate()}/${new Date().getUTCMonth()}/${new Date().getUTCFullYear()}`}
          />
          <DatePicker
            variant="inline"
            format="dd/MM/yyyy"
            margin="normal"
            fullWidth
            label="End Date"
            value={endDate}
            onChange={(value) => setEndDate(value)}
            placeholder={`${new Date().getUTCDate()}/${
              new Date().getUTCMonth() + 1
            }/${new Date().getUTCFullYear()}`}
          />
          <Box mt={1}>
            <Button variant="contained" color="primary" onClick={onExportClick}>
              Export
            </Button>
          </Box>
        </Box>
      </Paper>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      {isLoading && (
        <Box p={3}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  )
}
